/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { initializeApp } from "firebase/app";
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  browserPopupRedirectResolver,
} from "firebase/auth";
import { getDatabase, connectDatabaseEmulator } from "firebase/database";
import { connectAuthEmulator } from "firebase/auth";
import { Capacitor } from "@capacitor/core";

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
};

export const app = initializeApp(firebaseConfig);

// WKWebView (Capacitor iOS) persistence. The original April fix (commit
// 8bc1f8d) switched native to inMemoryPersistence because *indexedDB* hangs
// signInWithCredential indefinitely in WKWebView. But inMemory drops the
// session on every app kill, forcing a fresh SMS/OTP on every cold launch —
// which trips Firebase's `auth/too-many-requests` and re-asks OTP for a
// returning, already-verified user.
//
// Fix: persist with browserLocalPersistence (localStorage) on native. It was
// indexedDB that hung, NOT localStorage — localStorage is synchronous and
// reliable in WKWebView. A verified phone session now survives app restarts,
// so a returning user lands straight in with NO OTP. inMemoryPersistence is
// kept as the SECOND entry: Firebase tries each in order and falls back, so if
// localStorage is ever unavailable we degrade to in-memory rather than hang.
// (indexedDBLocalPersistence is deliberately NOT in the native list — it's the
// one that caused the original hang.)
//
// popupRedirectResolver: getAuth() wires this up automatically; initializeAuth()
// does NOT. Without it, signInWithPopup / signInWithRedirect throw
// `auth/argument-error` synchronously before opening any window — exactly the
// "Apple/Google do nothing on web" symptom we hit. Native (Capacitor) skips
// this because it uses the AppleSignIn + FirebaseAuthentication plugins
// directly and never enters the web popup path. Vitest/jsdom also skips it —
// the resolver's class init fails internal-assertion in jsdom.
const isTestEnv = import.meta.env?.MODE === "test";
export const auth = initializeAuth(app, {
  persistence: Capacitor.isNativePlatform()
    ? [browserLocalPersistence, inMemoryPersistence]
    : [indexedDBLocalPersistence, browserLocalPersistence],
  popupRedirectResolver:
    Capacitor.isNativePlatform() || isTestEnv
      ? undefined
      : browserPopupRedirectResolver,
});

export const db = getDatabase(app);

// DEV-ONLY: route auth + RTDB to the local Firebase Emulator Suite when
// VITE_USE_FIREBASE_EMULATOR === "true". This is gated on an opt-in env var
// that is absent in every production/preview build, so prod behaviour is
// completely unchanged. Used for two-account / multi-session QA where real
// phone-auth (reCAPTCHA + SMS) trips Firebase's project-wide
// `auth/too-many-requests` throttle. The emulator has no rate limit and
// accepts any test OTP, so two independent sessions (sim + web) can sign in
// as distinct uids instantly. Hosts: auth 127.0.0.1:9099, RTDB 127.0.0.1:9000
// (must match the ports in firebase.json). On native (Capacitor) the host is
// the dev machine's LAN IP, but this QA flow runs the web build, so localhost
// is correct.
if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true") {
  try {
    connectAuthEmulator(auth, "http://127.0.0.1:9099", {
      disableWarnings: true,
    });
    connectDatabaseEmulator(db, "127.0.0.1", 9000);
    // eslint-disable-next-line no-console
    console.info(
      "[firebase] Connected to LOCAL EMULATOR (auth:9099, db:9000) — DEV ONLY",
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[firebase] emulator connect failed:", err);
  }
}
